import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Eye } from 'lucide';

// common
import Icon from 'common/components/Icon';
import MuiLoading from 'common/components/MuiLoading';
import MuiTable from 'common/components/MuiTable';
import IconButtonTooltip from 'common/components/IconButtonTooltip';
import { PDF } from 'svg';
import { formatDateWithoutTimezone } from 'common/utils';

// screen
import { useTabsData } from 'modules/suppliers/detail/context/useTabsData';
import { pdfFileParser } from 'modules/suppliers/detail/components/modals/utils';
import PdfViewer from 'modules/suppliers/detail/components/modals/PdfViewer';
import CertificationDetails from 'modules/suppliers/detail/components/modals/details/CertificationDetails';
import { FoodSafetyCertificate } from 'modules/suppliers/services/useFoodSafetyCertificate';
import BackToSuppliersPageButton from 'modules/suppliers/detail/components/BackToSuppliersPageButton';
import { defaultPaginationParams } from 'modules/suppliers/detail/components/tab-panels/utils';

const CertificateOfFoodSafety = () => {
  const [openModal, setOpenModal] = useState<{
    pdfViewer: boolean;
    pdfUrl: string | null;
    pdfFileName: string;
    details: {
      open: boolean;
      data: FoodSafetyCertificate | null;
    };
  }>({
    pdfViewer: false,
    pdfUrl: null,
    pdfFileName: '',
    details: {
      open: false,
      data: null,
    },
  });

  const { certificateOfFoodSafety } = useTabsData();

  const onSortingChange = () => {
    // Sorting logic can be implemented here if needed
  };

  const onPageChange = () => {};
  const onRowsPerPageChange = () => {};

  const params = defaultPaginationParams;

  const handleOpenDetailsModal = (data: FoodSafetyCertificate) => {
    setOpenModal((prev) => ({
      ...prev,
      details: {
        open: true,
        data,
      },
    }));
  };

  const handleCloseDetailsModal = () => {
    setOpenModal((prev) => ({
      ...prev,
      details: { open: false, data: null },
    }));
  };

  const handleOpenPdfModal = (certificate: FoodSafetyCertificate) => {
    // Extract PDF path from certificate data
    const pdfPath = (certificate as any).pdfPath;

    if (pdfPath) {
      const pdfPaths = Array.isArray(pdfPath) ? pdfPath : [pdfPath];
      if (pdfPaths.length > 0) {
        const firstPdfPath = pdfPaths[0] as string;
        const pdf = pdfFileParser(firstPdfPath);

        setOpenModal((prev) => ({
          ...prev,
          pdfViewer: true,
          pdfUrl: pdf.to,
          pdfFileName: pdf.fileName || certificate.certificateNumber + '.pdf',
        }));
      }
    }
  };

  const handleClosePdfModal = () => {
    setOpenModal((prev) => ({
      ...prev,
      pdfViewer: false,
      pdfUrl: null,
      pdfFileName: '',
    }));
  };

  if (!certificateOfFoodSafety) return <MuiLoading />;

  return (
    <>
      <MuiTable<FoodSafetyCertificate>
        data={certificateOfFoodSafety || []}
        state={{ isLoading: false }}
        manualSorting={false}
        onSortingChange={onSortingChange}
        paginationProps={{
          count: certificateOfFoodSafety.length || 0,
          page: params.pageIndex,
          rowsPerPage: params.pageSize,
          onPageChange,
          onRowsPerPageChange,
        }}
        columns={[
          {
            accessorKey: 'rowNumber',
            header: 'STT',
            Header: () => 'STT',
            size: 60,
            enableSorting: false,
            Cell: ({ row }) => {
              const rowIndex = row.index + 1; // row.index is 0-based
              const offset = (params.pageIndex - 1) * params.pageSize;
              return offset + rowIndex;
            },
          },
          {
            accessorKey: 'certificateNumber',
            header: 'certificateNumber',
            Header: () => <FormattedMessage id="supplier.foodSafetyCertification.number" />,
            enableSorting: true,
          },
          {
            accessorKey: 'name',
            header: 'name',
            Header: () => <FormattedMessage id="supplier.foodSafetyCertification.name" />,
          },
          {
            accessorKey: 'type',
            header: 'type',
            Header: () => (
              <FormattedMessage id="supplier.foodSafetyCertification.documentClassification" />
            ),
          },
          {
            accessorKey: 'qualityStandard',
            header: 'qualityStandard',
            Header: () => (
              <FormattedMessage id="supplier.foodSafetyCertification.productStandardAbbreviation" />
            ),
          },
          {
            accessorKey: 'publishedAt',
            header: 'publishedAt',
            Header: () => <FormattedMessage id="supplier.foodSafetyCertification.issueDate" />,
            Cell: ({ renderedCellValue }) => {
              if (!renderedCellValue) return '';
              return formatDateWithoutTimezone(renderedCellValue.toString());
            },
            enableSorting: true,
          },
          {
            accessorKey: 'expiredAt',
            header: 'expiredAt',
            Header: () => <FormattedMessage id="supplier.foodSafetyCertification.expirationDate" />,
            Cell: ({ row: { original } }) => {
              const noExpiration = original.isUnlimited;
              if (noExpiration) return <FormattedMessage id="noExpiration" />;

              if (!original.expiredAt) return '';
              return formatDateWithoutTimezone(original.expiredAt.toString());
            },
            enableSorting: true,
          },
          {
            accessorKey: 'issuedBy',
            header: 'issuedBy',
            Header: () => <FormattedMessage id="supplier.foodSafetyCertification.issuePlace" />,
            enableSorting: true,
          },
        ]}
        enableRowActions
        renderRowActions={({ row: { original } }) => {
          const pdfDisabled = !original.hasPdf;

          return [
            <IconButtonTooltip
              aria-label="Xem nhanh"
              tooltipProps={{ title: <FormattedMessage id="quickView" /> }}
              onClick={() => handleOpenDetailsModal(original)}
            >
              <Icon icon={Eye} />
            </IconButtonTooltip>,
            <IconButtonTooltip
              aria-label="Xem PDF"
              tooltipProps={{ title: <FormattedMessage id="showPdf" /> }}
              onClick={() => handleOpenPdfModal(original)}
              disabled={pdfDisabled}
            >
              <PDF />
            </IconButtonTooltip>,
          ];
        }}
      />

      <BackToSuppliersPageButton />

      {/* Modals */}
      {openModal.details.open && openModal.details.data && (
        <CertificationDetails
          open={openModal.details.open}
          onClose={handleCloseDetailsModal}
          item={openModal.details.data}
        />
      )}
      {openModal.pdfViewer && (
        <PdfViewer
          open={openModal.pdfViewer}
          onClose={handleClosePdfModal}
          pdfUrl={openModal.pdfUrl}
          title={openModal.pdfFileName || 'PDF Document'}
        />
      )}
    </>
  );
};

export default CertificateOfFoodSafety;
